import React, { useState } from 'react';
import AsyncSelect from 'react-select/async';
import School from '../../../models/School';
import axios from 'axios';
import style from './MovePoses.module.sass';

const SearchForm = ({ onLoad }) => {
  const [schoolId, setSchoolId] = useState(null);
  const [query, setQuery] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [newSeason, setNewSeason] = useState('');
  const [creatingNewSeason, setCreatingNewSeason] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadSchoolOptions = (value, callback) => {
    School
      .where({
        sales_stat: { match: 'CUST' },
        name: { match: value },
      })
      .order({ name: 'asc' })
      .all()
      .then((res) => {
        const options = res.data.map((e) => ({ label: e.name, value: e.id }));
        callback(options);
      });
  };

  const searchStudents = () => {
    if (!schoolId || !query) return;
    setLoading(true);
    axios
      .get('/admin/student_images/search_students', { params: { school_id: schoolId, query } })
      .then((res) => {
        setStudents(res.data.students);
        setSelectedStudentId(null);
        setSeasons([]);
        setSelectedSeason('');
        setNewSeason('');
        setCreatingNewSeason(false);
      })
      .finally(() => setLoading(false));
  };

  const handleStudentSelect = (studentId) => {
    setSelectedStudentId(studentId);
    setSelectedSeason('');
    setNewSeason('');
    setCreatingNewSeason(false);
    axios
      .get('/admin/student_images/find_student_image', { params: { student_id: studentId } })
      .then((res) => {
        setSeasons(res.data.seasons || []);
      });
  };

  const handleSeasonChange = (value) => {
    if (value === '__new__') {
      setCreatingNewSeason(true);
      setSelectedSeason('');
      setNewSeason('');
    } else {
      setCreatingNewSeason(false);
      setNewSeason('');
      setSelectedSeason(value);
    }
  };

  const handleCreateAndLoad = () => {
    if (!selectedStudentId || !newSeason.trim()) return;
    setLoading(true);
    axios
      .post('/admin/student_images/create_student_image', {
        student_id: selectedStudentId,
        season: newSeason.trim()
      })
      .then((res) => {
        setSeasons(res.data.seasons || []);
        setSelectedSeason(res.data.student_image.season);
        setCreatingNewSeason(false);
        setNewSeason('');
        onLoad({
          studentImage: res.data.student_image,
          poses: []
        });
      })
      .catch(() => {
        alert('Failed to create student image');
      })
      .finally(() => setLoading(false));
  };

  const handleLoad = () => {
    if (!selectedStudentId || !selectedSeason) return;
    setLoading(true);
    axios
      .get('/admin/student_images/find_student_image', {
        params: { student_id: selectedStudentId, season: selectedSeason }
      })
      .then((res) => {
        onLoad({
          studentImage: res.data.student_image,
          poses: res.data.poses.map((p) => ({
            id: p.id,
            url: p.url,
            imageUrl: p.image_url,
            bestPose: p.best_pose
          }))
        });
      })
      .catch(() => {
        alert('No student image found for that season');
      })
      .finally(() => setLoading(false));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchStudents();
    }
  };

  return (
    <div className={style.searchForm}>
      <div className={style.formGroup}>
        <label>School</label>
        <AsyncSelect
          cacheOptions
          defaultOptions
          placeholder="Search for a school..."
          loadOptions={loadSchoolOptions}
          onChange={(opt) => {
            setSchoolId(opt.value);
            setStudents([]);
            setSelectedStudentId(null);
            setSeasons([]);
            setSelectedSeason('');
            setNewSeason('');
            setCreatingNewSeason(false);
          }}
        />
      </div>

      {schoolId && (
        <div className={style.formGroup}>
          <label>Search Student</label>
          <div className={style.searchRow}>
            <input
              type="text"
              className="form-control"
              placeholder="Name or Student ID"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className="btn btn-primary" onClick={searchStudents} disabled={loading}>
              Search
            </button>
          </div>
        </div>
      )}

      {students.length > 0 && (
        <div className={style.formGroup}>
          <label>Select Student</label>
          <select
            className="form-control"
            value={selectedStudentId || ''}
            onChange={(e) => handleStudentSelect(e.target.value)}
          >
            <option value="">-- Select --</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.full_name} (ID: {s.student_id}, Grade: {s.grade})
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedStudentId && (
        <div className={style.formGroup}>
          <label>Season</label>
          <select
            className="form-control"
            value={creatingNewSeason ? '__new__' : selectedSeason}
            onChange={(e) => handleSeasonChange(e.target.value)}
          >
            <option value="">-- Select --</option>
            {seasons.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
            <option value="__new__">+ Create new season...</option>
          </select>
        </div>
      )}

      {creatingNewSeason && (
        <div className={style.formGroup}>
          <label>New Season Name</label>
          <div className={style.searchRow}>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. fall2026, spring2026"
              value={newSeason}
              onChange={(e) => setNewSeason(e.target.value)}
            />
            <button
              className="btn btn-success"
              onClick={handleCreateAndLoad}
              disabled={loading || !newSeason.trim()}
            >
              {loading ? 'Creating...' : 'Create & Load'}
            </button>
          </div>
        </div>
      )}

      {selectedStudentId && selectedSeason && !creatingNewSeason && (
        <button className="btn btn-success" onClick={handleLoad} disabled={loading}>
          {loading ? 'Loading...' : 'Load Poses'}
        </button>
      )}
    </div>
  );
};

export default SearchForm;
